/** Pure Ready-to-deliver + logistics eligibility (Q16). No I/O. */

export type ReadyDealLike = {
  status?: string | null;
  ready_to_deliver?: boolean | null;
};

export type ReadyUnitLike = {
  ops_deal_id?: string | null;
  build_completed_at?: string | null;
  staged_at?: string | null;
  current_phase_id?: string | null;
};

export function dealAllowsLogistics(deal: ReadyDealLike | null | undefined): boolean {
  if (!deal) return false;
  if (deal.status === "archived") return false;
  return deal.ready_to_deliver === true;
}

export function unitBuildReadyForLogistics(
  unit: ReadyUnitLike,
  phase?: { is_build_complete?: boolean } | null
): boolean {
  if (unit.staged_at) return true;
  if (unit.build_completed_at) return true;
  return phase?.is_build_complete === true;
}

export function unitEligibleForLogisticsQueue(
  unit: ReadyUnitLike,
  deal: ReadyDealLike | null | undefined,
  phase?: { is_build_complete?: boolean } | null
): boolean {
  if (!unit.ops_deal_id) return false;
  return dealAllowsLogistics(deal) && unitBuildReadyForLogistics(unit, phase);
}

/**
 * Ready to deliver is logistics, not send-to-plant.
 * Custom builds with an in-app manufacturer unit cannot check it until Final QC / ship.
 * Inventory-only units and deals with no Kanban unit stay unchecked-gate.
 */
export function readyToDeliverNeedsBuildComplete(unit: {
  sale_path?: string | null;
} | null | undefined): boolean {
  if (!unit) return false;
  return unit.sale_path !== "inventory_no_sale_yet";
}

export function canMarkReadyToDeliver(args: {
  unit: (ReadyUnitLike & { sale_path?: string | null }) | null | undefined;
  phase?: { is_build_complete?: boolean } | null;
}): boolean {
  if (!readyToDeliverNeedsBuildComplete(args.unit)) return true;
  if (!args.unit) return true;
  return unitBuildReadyForLogistics(args.unit, args.phase);
}

export function readyToDeliverBlockedReason(args: {
  unit: (ReadyUnitLike & { sale_path?: string | null }) | null | undefined;
  phase?: { is_build_complete?: boolean } | null;
}): string | null {
  if (canMarkReadyToDeliver(args)) return null;
  return "Ready to deliver is available after the plant finishes this unit (Build complete / Ship from plant). Use Submit to build for paperwork.";
}

export function readyToDeliverTimelineBody(ready: boolean, holdNotes: string): string {
  if (ready) return "Marked ready to deliver. Logistics can pick up this deal when the building is finished.";
  const notes = holdNotes.trim();
  return notes
    ? `Ready to deliver cleared. Hold: ${notes}`
    : "Ready to deliver cleared. Deal left the logistics queue.";
}
